import type { Dispatch, UnknownAction } from "@reduxjs/toolkit";
import { useDispatch } from "react-redux";

import { useToast } from "@/components/ui/use-toast";
import {
  invalidatePro014EditSnapshot,
  useActivatePro014ItemMutation,
  useAddPro014CampaignItemsMutation,
  useDeactivatePro014ItemMutation,
  useReplaceItemGradeTargetsMutation,
  useUpdatePro014CampaignMutation,
  useUpdatePro014GradeRestrictionMutation,
} from "@/services/campaign-pro014";

import { NO_CHANGES_TOAST } from "../../utils/messages";
import { runPro014EditPlan } from "../../utils/pro014-edit-runner";
import { buildPro014EditPlan, hasPro014EditChanges } from "../../utils/pro014-update-diff";
import type {
  Pro014CampaignData,
  Pro014EditGateway,
  Pro014EditPlan,
  Pro014EditSource,
} from "../types";

const UNKNOWN_CAMPAIGN_CODE = 0;

export function usePro014EditGateway(campaignCode: number): Pro014EditGateway {
  const [updateCampaign] = useUpdatePro014CampaignMutation();
  const [updateRestriction] = useUpdatePro014GradeRestrictionMutation();
  const [addItems] = useAddPro014CampaignItemsMutation();
  const [replaceGrades] = useReplaceItemGradeTargetsMutation();
  const [activateItem] = useActivatePro014ItemMutation();
  const [deactivateItem] = useDeactivatePro014ItemMutation();

  return {
    updateFields: (fields) => updateCampaign({ code: campaignCode, ...fields }).unwrap(),
    setGradeRestriction: (enabled) => updateRestriction({ code: campaignCode, enabled }).unwrap(),
    addItems: (itemCodes, gradeTargets) =>
      addItems({ code: campaignCode, itemCodes, gradeTargets }).unwrap(),
    replaceGrades: ({ itemCode, targets }) =>
      replaceGrades({ code: campaignCode, itemCode, targets }).unwrap(),
    activateItem: (itemCode) => activateItem({ code: campaignCode, itemCode }).unwrap(),
    deactivateItem: (itemCode) => deactivateItem({ code: campaignCode, itemCode }).unwrap(),
  };
}

export function usePro014EditSubmit(
  editSource: Pro014EditSource | undefined,
): (data: Pro014CampaignData) => Promise<boolean> {
  const { toast } = useToast();
  const dispatch: Dispatch<UnknownAction> = useDispatch();
  const gateway: Pro014EditGateway = usePro014EditGateway(
    editSource?.campaignCode ?? UNKNOWN_CAMPAIGN_CODE,
  );

  return async (data: Pro014CampaignData): Promise<boolean> => {
    if (!editSource) return false;
    const plan: Pro014EditPlan = buildPro014EditPlan(editSource, data);
    if (!hasPro014EditChanges(plan)) {
      toast(NO_CHANGES_TOAST);
      return false;
    }
    try {
      await runPro014EditPlan(plan, gateway);
    } finally {
      dispatch(invalidatePro014EditSnapshot(editSource.campaignCode));
    }
    return true;
  };
}
